import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef, useMemo } from "react";
import { io, Socket } from "socket.io-client";
import { Environment } from "../../../core/config/environment";
import { useAutenticacaoContext } from "../../../core/contexts/AutenticacaoContext";
import { useNotificacoesContext } from "../../../core/contexts/NotificacoesContext";
import { useMensagens } from "../../hooks/atendimento/useMensagens";
import { IResumoNaoLidas } from "../../interface/atendimento/IResumoNaoLidas";
import { IOperadorConectado } from "../../interface/atendimento/websocket/IOperadorConectado";
import { IWebSocketEstadoAtendimento } from "../../interface/atendimento/websocket/IWebSocketEstadoAtendimento";
import { IWebSocketChatAtribuido } from "../../interface/atendimento/websocket/IWebSocketChatAtribuido";

export interface IAtribuicaoChat {
    operadorUuid: string;
    nome: string;
}

const HEARTBEAT_PRESENCA_MS = 30000;
const MODULO_ATENDIMENTO = "Atendimento";
const RESUMO_ZERADO: IResumoNaoLidas = { totalMensagens: 0, totalConversas: 0 };

/**
 * Identidade do operador: usa o uuid REAL do usuário logado (@operadorUuid).
 * Fallback para um id estável por navegador caso o uuid ainda não esteja disponível
 * (ex.: socket conecta antes de /meu-usuario resolver).
 */
const obterOperadorUuid = (): string => {
    const userUuid = localStorage.getItem('@operadorUuid');
    if (userUuid) return userUuid;

    let id = localStorage.getItem('@operadorSessaoId');
    if (!id) {
        id = (typeof crypto !== 'undefined' && crypto.randomUUID)
            ? crypto.randomUUID()
            : `op-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        localStorage.setItem('@operadorSessaoId', id);
    }
    return id;
};

interface IWebSocketAtendimentoContext {
    socket: Socket | null;
    isConnected: boolean;
    isConnecting: boolean;
    reconnectionAttempt: number;
    error: string | null;
    foiSubstituido: boolean;
    reconnect: () => void;
    assumirSessao: () => void;
    // Multi-operador
    operadores: IOperadorConectado[];
    atribuicoes: Map<string, IAtribuicaoChat>;
    operadorUuidAtual: string | null;
    assumirChat: (chatUuid: string) => void;
    liberarChat: (chatUuid: string) => void;
    // Notificações
    resumoNaoLidas: IResumoNaoLidas;
    /** Esta aba está operando o chat (presença e atribuição de conversas). */
    atendimentoAtivo: boolean;
    registrarAtendimento: () => void;
    liberarAtendimento: () => void;
}

const WebSocketAtendimentoContext = createContext<IWebSocketAtendimentoContext>({
    socket: null,
    isConnected: false,
    isConnecting: false,
    reconnectionAttempt: 0,
    error: null,
    foiSubstituido: false,
    reconnect: () => {},
    assumirSessao: () => {},
    operadores: [],
    atribuicoes: new Map(),
    operadorUuidAtual: null,
    assumirChat: () => {},
    liberarChat: () => {},
    resumoNaoLidas: RESUMO_ZERADO,
    atendimentoAtivo: false,
    registrarAtendimento: () => {},
    liberarAtendimento: () => {},
});

export const useWebSocketAtendimentoContext = () => {
    return useContext(WebSocketAtendimentoContext);
};

interface WebSocketAtendimentoProviderProps {
    children: ReactNode;
}

/**
 * Conexão de atendimento do sistema — vive acima das rotas, em qualquer tela.
 *
 * A aba conecta em modo `notificacoes`: entra apenas na sala do estabelecimento
 * para receber mensagens novas e o resumo de não lidas que alimenta o badge do
 * menu. A tela de conversas chama `registrarAtendimento()` e só então esta aba
 * passa a valer como operador (presença, atribuição de conversas e sessão única
 * por operador). Com isso o badge continua atualizando em todas as abas mesmo
 * quando outra assume o atendimento, e cada aba mantém uma única conexão.
 */
export const WebSocketAtendimentoProvider: React.FC<WebSocketAtendimentoProviderProps> = ({ children }) => {
    const { estabelecimento, perfil, verificarAcessoModulo } = useAutenticacaoContext();
    const { definirContador } = useNotificacoesContext();
    const { buscarResumoNaoLidas } = useMensagens();

    const [socket, setSocket] = useState<Socket | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [reconnectionAttempt, setReconnectionAttempt] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [foiSubstituido, setFoiSubstituido] = useState(false);
    const [atendimentoAtivo, setAtendimentoAtivo] = useState(false);
    const [resumoNaoLidas, setResumoNaoLidas] = useState<IResumoNaoLidas>(RESUMO_ZERADO);

    // Estado multi-operador (presença e atribuição de chats)
    const [operadores, setOperadores] = useState<IOperadorConectado[]>([]);
    const [atribuicoes, setAtribuicoes] = useState<Map<string, IAtribuicaoChat>>(new Map());
    const operadorUuidAtual = obterOperadorUuid();

    // Refs para o socket poder se restabelecer sem recriar a conexão a cada render.
    const salaChatRef = useRef<string | null>(null);
    const modoAtendimentoRef = useRef(false);
    const isSubstituidoRef = useRef(false);
    // Versão do resumo vindo do socket: descarta snapshot HTTP que chegou atrasado.
    const versaoResumoRef = useRef(0);
    const sincronizarResumoRef = useRef<() => void>(() => {});

    // Só abre conexão para quem realmente usa atendimento — evita uma conexão
    // por aba de todo usuário do sistema.
    const podeConectar = useMemo(
        () => !!estabelecimento && verificarAcessoModulo(MODULO_ATENDIMENTO),
        // `verificarAcessoModulo` é recriado a cada render — o que importa é o perfil.
        [estabelecimento, perfil]
    );

    /** Lê o resumo de não lidas da API (carga inicial e reconexões). */
    const sincronizarResumo = useCallback(async () => {
        const versao = versaoResumoRef.current;
        const resumo = await buscarResumoNaoLidas();

        // Um evento em tempo real chegou durante a requisição: ele é mais novo.
        if (!resumo || versao !== versaoResumoRef.current) return;

        setResumoNaoLidas(resumo);
    }, [buscarResumoNaoLidas]);

    useEffect(() => {
        sincronizarResumoRef.current = sincronizarResumo;
    }, [sincronizarResumo]);

    const connect = useCallback(() => {
        // Obter token JWT do localStorage
        const token = localStorage.getItem(Environment.TOKEN_KEY);
        const defaultToken = localStorage.getItem(Environment.TOKEN_ESTABELECIMENTO_KEY);

        if (!token && !defaultToken) {
            setError("Token de autenticação não encontrado");
            return;
        }

        setIsConnecting(true);
        setError(null);

        // Criar conexão Socket.IO com autenticação + identidade do operador (para presença/atribuição)
        const newSocket = io(Environment.MS_MARKETPLACES_SOCKET, {
            auth: {
                token: defaultToken || token,
                operadorUuid: obterOperadorUuid(),
                operadorNome: localStorage.getItem('@operadorNome') || undefined,
                // Conexão leve por padrão: a tela de conversas promove quando precisa.
                modo: 'notificacoes',
            },
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 2000,
            reconnectionDelayMax: 15000,
            randomizationFactor: 0.3,
        });

        // A sala de chat atual é registrada pelo hook `useWebSocketAtendimento`.
        (newSocket as any).setCurrentChatRoom = (chatUuid: string | null) => {
            salaChatRef.current = chatUuid;
        };

        // Evento: Conexão estabelecida
        newSocket.on('connect', () => {
            setIsConnected(true);
            setIsConnecting(false);
            setReconnectionAttempt(0);
            setError(null);

            // Restaurar o papel desta aba: sala do chat aberto e atendimento.
            if (salaChatRef.current) {
                newSocket.emit('entrar-chat', { chatUuid: salaChatRef.current });
            }

            if (modoAtendimentoRef.current) {
                newSocket.emit('registrar-atendimento');
            }

            // O resumo pode ter mudado enquanto a aba estava sem conexão.
            sincronizarResumoRef.current();
        });

        // Evento: Sessão substituída por outro navegador/dispositivo/aba.
        // A conexão continua viva (em modo notificações) — só o atendimento sai daqui.
        newSocket.on('sessao-substituida', () => {
            isSubstituidoRef.current = true;
            modoAtendimentoRef.current = false;
            setAtendimentoAtivo(false);
            setFoiSubstituido(true);
        });

        // Evento: Desconectado
        newSocket.on('disconnect', (reason) => {
            setIsConnected(false);
            if (reason === 'io server disconnect' && !isSubstituidoRef.current) {
                // O servidor forçou a desconexão por outro motivo — tentar reconectar
                newSocket.connect();
            }
        });

        // Evento: Erro de conexão
        newSocket.on('connect_error', (err) => {
            setIsConnecting(false);
            setError(`Erro de conexão: ${err.message}`);
        });

        // Evento: Tentativa de reconexão
        newSocket.on('reconnect_attempt', (attemptNumber: number) => {
            setIsConnecting(true);
            setReconnectionAttempt(attemptNumber);
        });

        // Evento: Reconectado com sucesso
        newSocket.on('reconnect', () => {
            setIsConnected(true);
            setIsConnecting(false);
            setReconnectionAttempt(0);
            setError(null);
        });

        // Evento: Falha ao reconectar (não dispara com Infinity, mas mantido por segurança)
        newSocket.on('reconnect_failed', () => {
            setIsConnecting(false);
            setError('Falha ao reconectar ao servidor');
        });

        // Evento: Erro do WebSocket
        newSocket.on('erro', (data: { mensagem: string }) => {
            setError(data.mensagem);
        });

        // ===== Notificações: total de mensagens não lidas do estabelecimento =====
        newSocket.on('resumo-nao-lidas', (data: IResumoNaoLidas) => {
            versaoResumoRef.current += 1;
            setResumoNaoLidas({
                totalMensagens: Number(data?.totalMensagens) || 0,
                totalConversas: Number(data?.totalConversas) || 0,
            });
        });

        // ===== Multi-operador: snapshot inicial do estado de atendimento =====
        newSocket.on('estado-atendimento', (data: IWebSocketEstadoAtendimento) => {
            const lista = data.operadores ?? [];
            setOperadores(lista);

            const nomePorUuid = new Map(lista.map(o => [o.operadorUuid, o.nome]));
            const mapa = new Map<string, IAtribuicaoChat>();
            Object.entries(data.atribuicoes ?? {}).forEach(([chatUuid, opUuid]) => {
                mapa.set(chatUuid, { operadorUuid: opUuid, nome: nomePorUuid.get(opUuid) ?? 'Operador' });
            });
            setAtribuicoes(mapa);
        });

        // Lista de operadores conectados mudou (entrou/saiu alguém)
        newSocket.on('operadores-conectados', (data: { operadores: IOperadorConectado[] }) => {
            setOperadores(data.operadores ?? []);
        });

        // Um chat foi atribuído (ou reassumido) por um operador
        newSocket.on('chat-atribuido', (data: IWebSocketChatAtribuido) => {
            setAtribuicoes(prev => {
                const mapa = new Map(prev);
                mapa.set(data.chatUuid, { operadorUuid: data.operadorUuid, nome: data.nome });
                return mapa;
            });
        });

        // Um chat foi liberado (manualmente ou por desconexão do dono)
        newSocket.on('chat-liberado', (data: { chatUuid: string }) => {
            setAtribuicoes(prev => {
                const mapa = new Map(prev);
                mapa.delete(data.chatUuid);
                return mapa;
            });
        });

        setSocket(newSocket);

        return newSocket;
    }, []);

    const reconnect = useCallback(() => {
        if (socket) {
            socket.disconnect();
            socket.connect();
        } else {
            connect();
        }
    }, [socket, connect]);

    /** Esta aba passa a operar o chat (presença, atribuições e sessão única). */
    const registrarAtendimento = useCallback(() => {
        modoAtendimentoRef.current = true;
        isSubstituidoRef.current = false;
        setAtendimentoAtivo(true);
        setFoiSubstituido(false);

        if (!socket) return;

        if (socket.connected) {
            socket.emit('registrar-atendimento');
            return;
        }

        // Ao conectar, o handler de `connect` reemite o registro.
        socket.connect();
    }, [socket]);

    /** Volta esta aba ao modo leve: sai da presença e libera as conversas assumidas. */
    const liberarAtendimento = useCallback(() => {
        modoAtendimentoRef.current = false;
        setAtendimentoAtivo(false);

        if (socket?.connected) {
            socket.emit('liberar-atendimento');
        }
    }, [socket]);

    const assumirSessao = useCallback(() => {
        registrarAtendimento();
    }, [registrarAtendimento]);

    const assumirChat = useCallback((chatUuid: string) => {
        socket?.emit('assumir-chat', { chatUuid });
    }, [socket]);

    const liberarChat = useCallback((chatUuid: string) => {
        socket?.emit('liberar-chat', { chatUuid });
    }, [socket]);

    // Heartbeat de presença — só faz sentido para a aba que está operando o chat
    useEffect(() => {
        if (!socket || !isConnected || !atendimentoAtivo) return;

        const intervalo = setInterval(() => {
            socket.emit('presenca-ping');
        }, HEARTBEAT_PRESENCA_MS);

        return () => clearInterval(intervalo);
    }, [socket, isConnected, atendimentoAtivo]);

    useEffect(() => {
        if (!podeConectar) {
            setResumoNaoLidas(RESUMO_ZERADO);
            return;
        }

        // Snapshot imediato: o badge aparece mesmo antes do socket subir.
        sincronizarResumoRef.current();

        const socketInstance = connect();

        return () => {
            if (!socketInstance) return;

            if (salaChatRef.current) {
                socketInstance.emit('sair-chat', { chatUuid: salaChatRef.current });
            }
            if (modoAtendimentoRef.current) {
                socketInstance.emit('liberar-atendimento');
            }

            socketInstance.disconnect();
            setSocket(null);
            setIsConnected(false);
            setAtendimentoAtivo(false);
            modoAtendimentoRef.current = false;
            salaChatRef.current = null;
        };
    }, [podeConectar, connect]);

    // Publica o total para o menu do sistema desenhar o badge.
    useEffect(() => {
        definirContador("conversas", podeConectar ? resumoNaoLidas.totalMensagens : 0);
    }, [definirContador, podeConectar, resumoNaoLidas]);

    return (
        <WebSocketAtendimentoContext.Provider
            value={{
                socket,
                isConnected,
                isConnecting,
                reconnectionAttempt,
                error,
                foiSubstituido,
                reconnect,
                assumirSessao,
                operadores,
                atribuicoes,
                operadorUuidAtual,
                assumirChat,
                liberarChat,
                resumoNaoLidas,
                atendimentoAtivo,
                registrarAtendimento,
                liberarAtendimento,
            }}
        >
            {children}
        </WebSocketAtendimentoContext.Provider>
    );
};
